/**
 * Category Library — central admin for the per-org custom
 * categories the studio adds via every form's "+ Add new" picker.
 *
 * Four scopes, one section each:
 *   Expense      — Org Finance > Expense form
 *   Income       — Org Finance > Income form
 *   Payment In   — Project transaction (client → studio)
 *   Payment Out  — Project transaction (studio → vendor)
 *
 * Each section shows BUILT-IN categories (with a small "Built-in"
 * badge, read-only — they're shipped by the app) and CUSTOM
 * categories (with edit, archive, delete). Custom-rename does NOT
 * retroactively rename historic rows (they keep the old label) —
 * same model as Notion tags.
 *
 * Permission: read is open to any org member so all pickers can
 * fetch their lists. Write (add / edit / archive / delete) is
 * gated server-side to SA / Admin / Manager via `canManageProjects`.
 * The UI hides write affordances when the caller can't write.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { canManageProjects } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import {
  createCustomCategory,
  deleteCustomCategory,
  updateCustomCategory,
  useCustomCategories,
  type CustomCategory,
  type CustomCategoryScope,
} from '@/lib/customCategories';
import {
  createStaffRole,
  deleteStaffRole,
  DEFAULT_STAFF_ROLES,
  useStaffRoles,
} from '@/lib/staffRoleLibrary';
import {
  createTaskCategory,
  deleteTaskCategory,
  DEFAULT_TASK_CATEGORIES,
  useTaskCategories,
} from '@/lib/taskCategoryLibrary';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';

// Built-in categories per scope. MUST stay in sync with the
// hardcoded lists in:
//   - lib/orgFinances.ts → ORG_FINANCE_CATEGORIES
//   - lib/transactions.ts → TRANSACTION_CATEGORY_OPTIONS_BY_TYPE
// (we import labels inline to avoid coupling this page to the
// types — labels are display-only here)
import { ORG_FINANCE_CATEGORIES } from '@/lib/orgFinances';
import { TRANSACTION_CATEGORY_OPTIONS_BY_TYPE } from '@/lib/transactions';

// All tab keys, including the legacy customCategories scopes
// plus the two extra org-scoped libraries (staff roles, task
// categories) that piggy-back here so admins manage every
// extensible list from one page.
type LibraryTabKey = CustomCategoryScope | 'staff_role' | 'task_category';

type TabDef = {
  key: LibraryTabKey;
  /** Short label for the chip strip. */
  label: string;
};

const TABS: TabDef[] = [
  { key: 'expense', label: 'Expense' },
  { key: 'income', label: 'Income' },
  { key: 'payment_in', label: 'Payment In' },
  { key: 'payment_out', label: 'Payment Out' },
  { key: 'staff_role', label: 'Staff Role' },
  { key: 'task_category', label: 'Task Category' },
];

type ScopeDef = {
  key: CustomCategoryScope;
  /** Short label for the tab chip. */
  label: string;
  /** Longer descriptive label shown inside the section header. */
  fullLabel: string;
  hint: string;
  builtIn: { value: string; label: string }[];
};

const SCOPES: ScopeDef[] = [
  {
    key: 'expense',
    label: 'Expense',
    fullLabel: 'Expense (Studio overhead)',
    hint: 'Used by Finance → Expenses. Rent, utilities, marketing, etc.',
    builtIn: ORG_FINANCE_CATEGORIES.map((c) => ({ value: c.key, label: c.label })),
  },
  {
    key: 'income',
    label: 'Income',
    fullLabel: 'Income (Studio incoming, non-project)',
    hint: 'Used by Finance → Expenses (income kind). Refunds, sponsor cheques, etc.',
    builtIn: ORG_FINANCE_CATEGORIES.map((c) => ({ value: c.key, label: c.label })),
  },
  {
    key: 'payment_in',
    label: 'Payment In',
    fullLabel: 'Payment In (from client)',
    hint: 'Used by Project → Add Transaction → Payment In. Token Amount, Design Advance, Milestone, etc.',
    builtIn: TRANSACTION_CATEGORY_OPTIONS_BY_TYPE.payment_in.map((o) => ({
      value: o.value,
      label: o.label,
    })),
  },
  {
    key: 'payment_out',
    label: 'Payment Out',
    fullLabel: 'Payment Out (studio spending)',
    hint: 'Used by Project → Add Transaction → Payment Out. Labour, Material, etc.',
    builtIn: TRANSACTION_CATEGORY_OPTIONS_BY_TYPE.payment_out.map((o) => ({
      value: o.value,
      label: o.label,
    })),
  },
];

export function CategoryLibraryPage() {
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { role } = useCurrentOrgRole();
  // Default canManage: SA / Admin / Manager — applies to the 4
  // customCategories scopes + task_category (mirrors
  // `canManageProjects` in firestore.rules).
  const canManage = canManageProjects(role);
  // Staff role library writes are `canOrgFinance` (SA / Admin /
  // Manager / Accountant) — accountants own the staff roster too.
  const canManageStaffRole = canManage || role === 'accountant';

  // URL-keyed active tab so deep links + back/forward work +
  // the choice survives refresh. Default to expense.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = (searchParams.get('scope') as LibraryTabKey | null) ?? null;
  const activeKey: LibraryTabKey =
    requested && TABS.some((t) => t.key === requested) ? requested : 'expense';

  const setActive = (key: LibraryTabKey) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('scope', key);
      return next;
    });
  };

  // Look up the active section's definition. Scope-shaped tabs
  // surface a fullLabel/hint/builtIn triple; the two extra tabs
  // are rendered by their own sub-components.
  const activeScope = SCOPES.find((s) => s.key === activeKey) ?? null;

  return (
    <div className="page active" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Category Library</h1>
          <div className="projects-stats">
            Built-in + your own categories used across every form
          </div>
        </div>
      </div>

      {!canManage ? (
        <div
          style={{
            margin: '8px 0 16px',
            padding: 12,
            borderRadius: 8,
            background: '#FEF3C7',
            color: '#92400E',
            fontSize: 13,
          }}
        >
          Read-only — ask a Super Admin, Admin, or Manager to add or edit
          custom categories.
        </div>
      ) : null}

      {/* Tab strip — one chip per scope. Mirrors the Finance hub
          sub-tab pattern so the muscle memory transfers. */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`filter-chip${activeKey === tab.key ? ' active' : ''}`}
            onClick={() => setActive(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Single active section — branched by tab key. */}
      {activeScope ? (
        <ScopeSection
          key={activeScope.key}
          scope={activeScope}
          orgId={orgId}
          canManage={canManage}
        />
      ) : activeKey === 'staff_role' ? (
        <StaffRoleSection orgId={orgId} canManage={canManageStaffRole} />
      ) : activeKey === 'task_category' ? (
        <TaskCategorySection orgId={orgId} canManage={canManage} />
      ) : null}
    </div>
  );
}

// ───── Per-scope section ───────────────────────────────────

function ScopeSection({
  scope,
  orgId,
  canManage,
}: {
  scope: ScopeDef;
  orgId: string | null;
  canManage: boolean;
}) {
  const { data: customs, loading } = useCustomCategories(orgId, scope.key);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CustomCategory | null>(null);

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{scope.fullLabel}</div>
          <div
            style={{
              marginTop: 2,
              fontSize: 12,
              color: 'var(--text-secondary)',
            }}
          >
            {scope.hint}
          </div>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setAdding(true)}
            style={{ flexShrink: 0 }}
          >
            <i className="ph ph-plus" /> Add
          </button>
        ) : null}
      </div>

      <div>
        {/* Built-in rows */}
        {scope.builtIn.map((b) => (
          <Row
            key={`builtin-${b.value}`}
            label={b.label}
            badge="Built-in"
            badgeTint="#E5E7EB"
            badgeColor="#6B7280"
          />
        ))}

        {/* Divider + custom rows */}
        {customs.length > 0 ? (
          <div
            style={{
              padding: '6px 16px',
              background: 'var(--surface-muted, #f8fafc)',
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              borderTop: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            Your studio added
          </div>
        ) : null}
        {customs.map((c) => (
          <Row
            key={c.id}
            label={c.label}
            badge="Custom"
            badgeTint="#DBEAFE"
            badgeColor="#1D4ED8"
            actions={
              canManage ? (
                <CustomRowActions
                  row={c}
                  onEdit={() => setEditing(c)}
                />
              ) : null
            }
          />
        ))}

        {/* Empty state when no customs + not loading */}
        {!loading && customs.length === 0 ? (
          <div
            style={{
              padding: 16,
              fontSize: 12,
              color: 'var(--text-secondary)',
              textAlign: 'center',
              borderTop: '1px solid var(--border)',
            }}
          >
            No custom categories yet
            {canManage ? '. Click Add to create your first.' : '.'}
          </div>
        ) : null}
      </div>

      {adding ? (
        <CategoryFormModal
          mode={{ kind: 'create', scope: scope.key }}
          orgId={orgId}
          existingSortOrders={customs.map((c) => c.sortOrder)}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {editing ? (
        <CategoryFormModal
          mode={{ kind: 'edit', row: editing }}
          orgId={orgId}
          existingSortOrders={customs.map((c) => c.sortOrder)}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}

// ───── Row primitive ───────────────────────────────────────

function Row({
  label,
  badge,
  badgeTint,
  badgeColor,
  actions,
}: {
  label: string;
  badge: string;
  badgeTint: string;
  badgeColor: string;
  actions?: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: '10px 16px',
        borderTop: '1px solid var(--border)',
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 10,
        alignItems: 'center',
      }}
    >
      <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>{label}</div>
      <span
        style={{
          padding: '2px 7px',
          borderRadius: 999,
          background: badgeTint,
          color: badgeColor,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        {badge.toUpperCase()}
      </span>
      <div style={{ minWidth: 80, textAlign: 'right' }}>{actions ?? <span />}</div>
    </div>
  );
}

function CustomRowActions({
  row,
  onEdit,
}: {
  row: CustomCategory;
  onEdit: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
      <button
        type="button"
        onClick={onEdit}
        title="Edit"
        style={{
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '4px 8px',
          fontSize: 12,
          cursor: 'pointer',
          color: 'var(--text-secondary)',
        }}
      >
        <i className="ph ph-pencil-simple" />
      </button>
      <button
        type="button"
        onClick={async () => {
          if (!confirm(`Archive "${row.label}"? It will hide from pickers but existing entries keep showing it.`)) return;
          await updateCustomCategory(row.id, { archived: true });
        }}
        title="Archive"
        style={{
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '4px 8px',
          fontSize: 12,
          cursor: 'pointer',
          color: 'var(--text-secondary)',
        }}
      >
        <i className="ph ph-archive" />
      </button>
      <button
        type="button"
        onClick={async () => {
          if (!confirm(`Delete "${row.label}"? Existing entries keep showing the label string (deleting only removes it from future pickers).`)) return;
          await deleteCustomCategory(row.id);
        }}
        title="Delete"
        style={{
          background: 'transparent',
          border: '1px solid #fecaca',
          borderRadius: 6,
          padding: '4px 8px',
          fontSize: 12,
          cursor: 'pointer',
          color: '#b91c1c',
        }}
      >
        <i className="ph ph-trash" />
      </button>
    </div>
  );
}

// ───── Add / Edit modal ────────────────────────────────────

function CategoryFormModal({
  mode,
  orgId,
  existingSortOrders,
  onClose,
}: {
  mode:
    | { kind: 'create'; scope: CustomCategoryScope }
    | { kind: 'edit'; row: CustomCategory };
  orgId: string | null;
  existingSortOrders: number[];
  onClose: () => void;
}) {
  const { user } = useAuth();
  const editing = mode.kind === 'edit' ? mode.row : null;

  const [label, setLabel] = useState(editing?.label ?? '');
  const [sortOrder, setSortOrder] = useState<string>(
    String(editing?.sortOrder ?? 0),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxSort = useMemo(
    () => (existingSortOrders.length ? Math.max(...existingSortOrders) : 0),
    [existingSortOrders],
  );

  async function save() {
    if (!label.trim()) {
      setError('Label is required.');
      return;
    }
    if (!orgId) {
      setError('Not ready — please reload.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const so = parseInt(sortOrder, 10);
      const validSo = Number.isFinite(so) ? so : maxSort + 10;
      if (mode.kind === 'create') {
        if (!user) throw new Error('Not signed in.');
        await createCustomCategory({
          orgId,
          scope: mode.scope,
          label,
          sortOrder: validSo,
          createdBy: user.uid,
        });
      } else {
        await updateCustomCategory(mode.row.id, { label, sortOrder: validSo });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          className="card"
          style={{
            width: '100%',
            maxWidth: 420,
            background: 'var(--surface)',
            borderRadius: 12,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16 }}>
              {mode.kind === 'create' ? 'Add custom category' : 'Edit custom category'}
            </h3>
            <button
              type="button"
              onClick={() => !busy && onClose()}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
              }}
            >
              <i className="ph ph-x" />
            </button>
          </div>

          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
                Label *
              </span>
              <input
                className="input"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.currentTarget.value)}
                placeholder="e.g. Diwali Bonus"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void save();
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
                Sort order
              </span>
              <input
                className="input"
                type="number"
                inputMode="numeric"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.currentTarget.value)}
              />
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                Lower numbers appear first. Leave 0 to let it land at the end automatically.
              </span>
            </label>
            {error ? (
              <div
                style={{
                  padding: 10,
                  borderRadius: 8,
                  background: '#FEE2E2',
                  color: '#B91C1C',
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            ) : null}
          </div>

          <div
            style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? 'Saving…' : mode.kind === 'create' ? 'Add' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ───── Staff Role section ──────────────────────────────────
//
// Reads `staffRoleLibrary` (org-scoped). Defaults come from
// `DEFAULT_STAFF_ROLES` (built-in roster) — those rows are
// labelled "Default" and not deletable. Custom rows show a
// trash button.

function StaffRoleSection({
  orgId,
  canManage,
}: {
  orgId: string | null;
  canManage: boolean;
}) {
  const { customs, loading } = useStaffRoles(orgId);
  const [adding, setAdding] = useState(false);

  const defaultKeys = useMemo(
    () => new Set(DEFAULT_STAFF_ROLES.map((r) => r.key)),
    [],
  );
  // Only show non-default rows in the CUSTOM band; defaults render
  // from the built-in list above the divider.
  const customRows = useMemo(
    () => customs.filter((c) => !defaultKeys.has(c.key)),
    [customs, defaultKeys],
  );

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Staff Roles</div>
          <div
            style={{
              marginTop: 2,
              fontSize: 12,
              color: 'var(--text-secondary)',
            }}
          >
            Used by Add Staff — Carpenter, Electrician, Site Engineer, etc.
            Writes require Super Admin / Admin / Manager / Accountant.
          </div>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setAdding(true)}
            style={{ flexShrink: 0 }}
          >
            <i className="ph ph-plus" /> Add
          </button>
        ) : null}
      </div>

      <div>
        {DEFAULT_STAFF_ROLES.map((r) => (
          <Row
            key={`default-${r.key}`}
            label={r.label}
            badge="Default"
            badgeTint="#E5E7EB"
            badgeColor="#6B7280"
          />
        ))}

        {customRows.length > 0 ? (
          <div
            style={{
              padding: '6px 16px',
              background: 'var(--surface-muted, #f8fafc)',
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              borderTop: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            Your studio added
          </div>
        ) : null}
        {customRows.map((c) => (
          <Row
            key={c.id}
            label={c.label}
            badge="Custom"
            badgeTint="#DBEAFE"
            badgeColor="#1D4ED8"
            actions={
              canManage ? (
                <SimpleRowActions
                  onDelete={async () => {
                    if (
                      !confirm(
                        `Delete "${c.label}"? Existing staff keep showing the label string (deleting only removes it from future pickers).`,
                      )
                    )
                      return;
                    await deleteStaffRole(c.id);
                  }}
                />
              ) : null
            }
          />
        ))}

        {!loading && customRows.length === 0 ? (
          <div
            style={{
              padding: 16,
              fontSize: 12,
              color: 'var(--text-secondary)',
              textAlign: 'center',
              borderTop: '1px solid var(--border)',
            }}
          >
            No custom roles yet
            {canManage ? '. Click Add to create your first.' : '.'}
          </div>
        ) : null}
      </div>

      {adding ? (
        <SimpleAddModal
          title="Add custom staff role"
          placeholder="e.g. Glass Fitter"
          orgId={orgId}
          onClose={() => setAdding(false)}
          onSubmit={async ({ orgId: id, label, uid }) => {
            await createStaffRole({ orgId: id, label, createdBy: uid });
          }}
        />
      ) : null}
    </section>
  );
}

// ───── Task Category section ───────────────────────────────
//
// Same shape as StaffRoleSection but backed by
// `taskCategoryLibrary`. Writes require SA / Admin / Manager.

function TaskCategorySection({
  orgId,
  canManage,
}: {
  orgId: string | null;
  canManage: boolean;
}) {
  const { customs, loading } = useTaskCategories(orgId);
  const [adding, setAdding] = useState(false);

  const defaultKeys = useMemo(
    () => new Set(DEFAULT_TASK_CATEGORIES.map((c) => c.key)),
    [],
  );
  const customRows = useMemo(
    () => customs.filter((c) => !defaultKeys.has(c.key)),
    [customs, defaultKeys],
  );

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Task Categories</div>
          <div
            style={{
              marginTop: 2,
              fontSize: 12,
              color: 'var(--text-secondary)',
            }}
          >
            Used by Project → Tasks — Electrical, Plumbing, Woodwork, etc.
            Writes require Super Admin / Admin / Manager.
          </div>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setAdding(true)}
            style={{ flexShrink: 0 }}
          >
            <i className="ph ph-plus" /> Add
          </button>
        ) : null}
      </div>

      <div>
        {DEFAULT_TASK_CATEGORIES.map((c) => (
          <Row
            key={`default-${c.key}`}
            label={c.label}
            badge="Default"
            badgeTint="#E5E7EB"
            badgeColor="#6B7280"
          />
        ))}

        {customRows.length > 0 ? (
          <div
            style={{
              padding: '6px 16px',
              background: 'var(--surface-muted, #f8fafc)',
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              borderTop: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            Your studio added
          </div>
        ) : null}
        {customRows.map((c) => (
          <Row
            key={c.id}
            label={c.label}
            badge="Custom"
            badgeTint="#DBEAFE"
            badgeColor="#1D4ED8"
            actions={
              canManage ? (
                <SimpleRowActions
                  onDelete={async () => {
                    if (
                      !confirm(
                        `Delete "${c.label}"? Existing tasks keep showing the label string (deleting only removes it from future pickers).`,
                      )
                    )
                      return;
                    await deleteTaskCategory(c.id);
                  }}
                />
              ) : null
            }
          />
        ))}

        {!loading && customRows.length === 0 ? (
          <div
            style={{
              padding: 16,
              fontSize: 12,
              color: 'var(--text-secondary)',
              textAlign: 'center',
              borderTop: '1px solid var(--border)',
            }}
          >
            No custom task categories yet
            {canManage ? '. Click Add to create your first.' : '.'}
          </div>
        ) : null}
      </div>

      {adding ? (
        <SimpleAddModal
          title="Add custom task category"
          placeholder="e.g. Curtain Fitting"
          orgId={orgId}
          onClose={() => setAdding(false)}
          onSubmit={async ({ orgId: id, label, uid }) => {
            await createTaskCategory({ orgId: id, label, createdBy: uid });
          }}
        />
      ) : null}
    </section>
  );
}

// Tiny row-action component — just a trash button. Used for the
// two simpler libraries (no archive / sort-order needed there).
function SimpleRowActions({ onDelete }: { onDelete: () => void | Promise<void> }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
      <button
        type="button"
        onClick={() => void onDelete()}
        title="Delete"
        style={{
          background: 'transparent',
          border: '1px solid #fecaca',
          borderRadius: 6,
          padding: '4px 8px',
          fontSize: 12,
          cursor: 'pointer',
          color: '#b91c1c',
        }}
      >
        <i className="ph ph-trash" />
      </button>
    </div>
  );
}

// Single-field add modal shared by Staff Role + Task Category.
// Both back-ends only need `label`; the caller's onSubmit wraps
// the right CRUD call.
function SimpleAddModal({
  title,
  placeholder,
  orgId,
  onClose,
  onSubmit,
}: {
  title: string;
  placeholder: string;
  orgId: string | null;
  onClose: () => void;
  onSubmit: (args: { orgId: string; label: string; uid: string }) => Promise<void>;
}) {
  const { user } = useAuth();
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!label.trim()) {
      setError('Label is required.');
      return;
    }
    if (!orgId || !user) {
      setError('Not ready — please reload.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ orgId, label: label.trim(), uid: user.uid });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          className="card"
          style={{
            width: '100%',
            maxWidth: 420,
            background: 'var(--surface)',
            borderRadius: 12,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
            <button
              type="button"
              onClick={() => !busy && onClose()}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
              }}
            >
              <i className="ph ph-x" />
            </button>
          </div>

          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
                Label *
              </span>
              <input
                className="input"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.currentTarget.value)}
                placeholder={placeholder}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void save();
                }}
              />
            </label>
            {error ? (
              <div
                style={{
                  padding: 10,
                  borderRadius: 8,
                  background: '#FEE2E2',
                  color: '#B91C1C',
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            ) : null}
          </div>

          <div
            style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
